import { ArrowLeft, BookOpen, BookPlus, NotebookPen, ScanText, Search } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { AllRecipes } from '@/components/AllRecipes'
import { BooksSection, NewBook } from '@/components/BooksSection'
import { CookbooksSection } from '@/components/CookbooksSection'
import { CreateAction } from '@/components/CreateAction'
import { FilterButton, type Filter } from '@/components/Filters'
import { FormError } from '@/components/FormError'
import { PickOne } from '@/components/PickOne'
import { ModernRecipes } from '@/components/recipes/ModernRecipes'
import { Pills } from '@/components/Pills'
import { SelectToggle } from '@/components/Selection'
import { page } from '@/components/layout'
import { RecipeTile } from '@/components/RecipeTile'
import { Input } from '@/components/ui/input'
import type { Me } from '@/lib/api'
import { useCircleId, useFamily, type Family } from '@/lib/families'
import { usePageFrame } from '@/lib/frame'
import { photoUrl } from '@/lib/photos'
import { NO_BOOK, RECIPES_BY, useBooks, type RecipesBy } from '@/lib/recipes'
import { remember, remembered } from '@/lib/remember'
import { useSelection } from '@/lib/selection'
import { useCurrentSpace } from '@/lib/spaces'
import { useMe } from '@/lib/useMe'
import { cn } from '@/lib/utils'
import { PageLoader } from '@/components/Loader'
import { BarRow } from '@/components/nav/BarRow'

// The pages of a space: its recipes, its people, and keeping a copy. Each loads the family first.
export function WithFamily({ children }: { children: (family: Family) => ReactNode }) {
  const id = useCircleId()
  const family = useFamily(id)
  if (family.isPending) return <PageLoader />
  if (family.isError) return <FormError error={family.error} />
  return children(family.data)
}

export const FamilyPage = ({ me }: { me: Me }) => (
  <WithFamily>{(family) => <FamilyHome family={family} me={me} />}</WithFamily>
)

// A place's heading, the way notesfrom heads its pages: a small eyebrow above a large title. The eyebrow is your
// home's name: the household you identify with, not the family container around it. On a phone the place's name sits
// at the left of the top bar instead, as on a page you've stepped into, and the heading here is left to screen readers
// so the page starts with what's in it. A greeting (Home's welcome) is part of the page rather than its name, so it
// stays.
export function PlaceHeader({
  family,
  title,
  greeting,
  children,
}: {
  family: Family
  title: string
  greeting?: boolean
  children?: ReactNode
}) {
  const home = family.home?.name ?? family.name
  return (
    <header className="flex flex-col gap-1.5">
      <div className={cn('flex flex-col gap-1.5', !greeting && 'max-nav:sr-only')}>
        <p className="hidden eyebrow nav:block">{home}</p>
        <h1 className="text-display">{title}</h1>
      </div>
      {children}
    </header>
  )
}

type RecipesView = 'books' | 'all'
const RECIPES_VIEW_KEY = 'gatheredfrom:recipes-view'
const RECIPES_BY_KEY = 'gatheredfrom:recipes-by'
const RECIPES_BY_LABEL: Record<RecipesBy, string> = { newest: 'Newest', name: 'A–Z', meal: 'Meal', book: 'Book' }

// The circle's recipes, two ways, as Photos has albums and the stream: the books on their shelf, or every recipe at
// once. Either can show only what you added yourself. The welcome is on Home (CircleHome.tsx).
function FamilyHome({ family }: { family: Family; me: Me }) {
  usePageFrame(family.id, { title: 'Recipes' })
  const space = useCurrentSpace(family)
  const books = useBooks(family.id, space?.id)
  const recent = books.data?.recent ?? []
  const [params, setParams] = useSearchParams()
  const fromUrl = params.get('view')
  const view: RecipesView =
    fromUrl === 'books' || fromUrl === 'all'
      ? fromUrl
      : remembered<RecipesView>(RECIPES_VIEW_KEY, ['books', 'all'], 'books')
  const byUrl = params.get('by') as RecipesBy | null
  const by = byUrl && RECIPES_BY.includes(byUrl) ? byUrl : remembered<RecipesBy>(RECIPES_BY_KEY, RECIPES_BY, 'newest')
  const mine = params.get('mine') === '1'
  // The recipes in no book, on their own (from the shelf's "Not in a book").
  const loose = params.get('loose') === '1'
  const selection = useSelection()
  const choose = (change: { view?: RecipesView; by?: RecipesBy; mine?: boolean }) => {
    if (change.view) remember(RECIPES_VIEW_KEY, change.view)
    if (change.by) remember(RECIPES_BY_KEY, change.by)
    const next = { view: change.view ?? view, by: change.by ?? by, mine: change.mine ?? mine }
    const query = new URLSearchParams({ view: next.view })
    if (next.view === 'all') query.set('by', next.by)
    if (next.mine) query.set('mine', '1')
    selection.stop()
    setParams(query, { replace: true })
  }
  // The switch shows once there's a book or a recipe to look at; before that the page is the invitation to start.
  const someBooks = (books.data?.books.length ?? 0) > 0
  const someRecipes = someBooks || (books.data?.loose?.recipes ?? 0) > 0
  // Searching looks through every recipe, whichever way the page is showing them.
  const [search, setSearch] = useState('')
  const showing = loose ? 'loose' : someRecipes ? (search.trim() ? 'all' : view) : 'books'
  // A recipe can go in a book, picked as it's started, or in none.
  const [starting, setStarting] = useState(false)
  const [adding, setAdding] = useState<'scan' | 'write' | null>(null)
  const navigate = useNavigate()
  const startRecipe = (bookId: string, how: 'scan' | 'write') =>
    navigate(
      `${bookId ? `/book/${bookId}/new` : `/circle/${family.id}/recipes/new`}${how === 'write' ? '?write=1' : ''}`,
    )
  const add = (how: 'scan' | 'write') => {
    if (!someBooks) startRecipe('', how)
    else setAdding(how)
  }

  // Modern shows the wall; the books and cookbooks keep their shelf a tap away (?shelf=1), as Traditional has them.
  const modern = useMe().data?.recipesStyle === 'modern'
  const shelf = params.get('shelf') === '1'
  const seen = useSeen(family.id)
  const create = books.data && !selection.selecting && (
    <CreateAction
      section="recipes"
      title="Add to recipes"
      actions={[
        { label: 'Scan a recipe card', icon: ScanText, onSelect: () => add('scan') },
        { label: 'Write a recipe', icon: NotebookPen, onSelect: () => add('write') },
        ...(loose ? [] : [{ label: 'New recipe book', icon: BookPlus, onSelect: () => setStarting(true) }]),
      ]}
    />
  )
  const sheets = books.data && (
    <>
      <NewBook
        key={`${space?.id}-${starting}`}
        open={starting}
        onOpenChange={setStarting}
        family={family}
        space={space}
        first={!someBooks}
      />
      <PickOne
        open={!!adding && !starting}
        onOpenChange={(open) => !open && setAdding(null)}
        title="Which book is it for?"
        description="A book is up to you. You can send it to one any time."
        icon={BookOpen}
        choices={[
          { id: '', title: 'No book', detail: 'Add it to Recipes on its own', cover: null },
          ...books.data.books
            .filter((x) => !x.elsewhere)
            .map((x) => {
              const cover = x.covers?.[0] ?? x.cover
              return {
                id: x.id,
                title: x.title,
                detail: `${x.recipes} ${x.recipes === 1 ? 'recipe' : 'recipes'}`,
                cover: cover ? photoUrl(cover, 'thumb') : null,
              }
            }),
        ]}
        onPick={(id) => startRecipe(id, adding ?? 'scan')}
        fresh={{ label: 'A new book', onSelect: () => setStarting(true) }}
      />
    </>
  )

  if (modern && !shelf && !loose)
    return (
      <div className="flex flex-col gap-6 pt-6 nav:gap-8 nav:pt-14">
        <div className={cn(page, 'flex flex-col gap-6')}>
          <div className="flex flex-wrap items-end justify-between gap-4 max-nav:sr-only">
            <PlaceHeader family={family} title="Recipes" />
          </div>
          {create}
          {someRecipes && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                aria-pressed={mine}
                onClick={() => setParams(mine ? {} : { mine: '1' }, { replace: true })}
                className={cn(
                  'inline-flex h-10 items-center rounded-full px-4 text-sm font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                  mine ? 'bg-brand text-brand-foreground' : 'border hover:bg-secondary',
                )}
              >
                Only mine
              </button>
              <Link
                to={`/circle/${family.id}/recipes?shelf=1&view=books${mine ? '&mine=1' : ''}`}
                className="inline-flex h-10 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors outline-none hover:bg-secondary focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <BookOpen className="size-4" aria-hidden /> Books and cookbooks
              </Link>
            </div>
          )}
          <ModernRecipes key={String(mine)} family={family} space={space} mine={mine} seenAt={seen} />
        </div>
        {sheets}
      </div>
    )

  return (
    <div className="flex flex-col gap-14 pt-6 nav:gap-16 nav:pt-14">
      <div className={cn(page, 'flex flex-col gap-8')}>
        {modern && (
          <Link
            to={`/circle/${family.id}/recipes${mine ? '?mine=1' : ''}`}
            className="-ml-2 flex items-center gap-1.5 self-start rounded-full py-1 pr-3 pl-2 text-sm outline-none hover:bg-secondary focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <ArrowLeft className="size-4" aria-hidden /> All recipes
          </Link>
        )}
        {/* On a phone the heading is in the top bar, so this row takes no room there. */}
        <div className="flex flex-wrap items-end justify-between gap-4 max-nav:sr-only">
          <PlaceHeader family={family} title="Recipes" />
        </div>
        {create}
        {someRecipes && (
          <RecipesBar
            view={view}
            by={by}
            mine={mine}
            loose={loose}
            search={search}
            onSearch={setSearch}
            onChange={choose}
            select={showing !== 'books' && <SelectToggle selection={selection} />}
          />
        )}
      </div>
      {showing === 'loose' ? (
        <div className={cn(page, 'flex flex-col gap-8')}>
          <div className="flex flex-col gap-3">
            <Link
              to={`/circle/${family.id}/recipes${mine ? '?mine=1' : ''}`}
              className="-ml-2 flex items-center gap-1.5 self-start rounded-full py-1 pr-3 pl-2 text-sm outline-none hover:bg-secondary focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <ArrowLeft className="size-4" aria-hidden /> All books
            </Link>
            <h2 className="text-title">Not in a book</h2>
            <p className="text-sub text-muted-foreground">
              Recipes added on their own. Pick some and send them to a book whenever you like.
            </p>
          </div>
          <AllRecipes
            key={`loose-${mine}`}
            family={family}
            space={space}
            by="newest"
            mine={mine}
            loose
            search={search}
            selection={selection}
          />
        </div>
      ) : showing === 'all' ? (
        <div className={page}>
          <AllRecipes
            key={String(mine)}
            family={family}
            space={space}
            by={by}
            mine={mine}
            search={search}
            selection={selection}
          />
        </div>
      ) : (
        <>
          <div className={page}>
            <BooksSection family={family} space={space} mine={mine} onStart={() => setStarting(true)} />
          </div>
          <div className={page}>
            <CookbooksSection family={family} space={space} hasRecipes={recent.length > 0} mine={mine} />
          </div>
          {/* Only mine has its own recipes in the all-recipes view; here it keeps to the shelves. */}
          {recent.length > 0 && !mine && (
            <section className={cn(page, 'flex flex-col gap-6')} aria-labelledby="recent">
              <h2 id="recent" className="text-title">
                Recently added
              </h2>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:grid-cols-6">
                {recent.map((recipe, i) => (
                  <li key={recipe.id}>
                    <RecipeTile recipe={recipe} index={i} line={recipe.book ?? NO_BOOK} />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
      {sheets}
    </div>
  )
}

// One row: books or every recipe at once, a box to find one by name, and behind one button how they're laid out (when
// they're all at once) and whether they're everyone's or only your own. Photos keeps its own the same way. The
// recipes in no book have just the search.
function RecipesBar({
  view,
  by,
  mine,
  loose,
  search,
  onSearch,
  onChange,
  select,
}: {
  view: RecipesView
  by: RecipesBy
  mine: boolean
  loose: boolean
  search: string
  onSearch: (search: string) => void
  onChange: (change: { view?: RecipesView; by?: RecipesBy; mine?: boolean }) => void
  select: ReactNode
}) {
  const filters: Filter[] = [
    ...(view === 'all'
      ? [
          {
            heading: 'Order',
            label: 'Lay out the recipes by',
            value: by,
            options: RECIPES_BY.map((x): [string, string] => [x, RECIPES_BY_LABEL[x]]),
            onChange: (b: string) => onChange({ by: b as RecipesBy }),
          },
        ]
      : []),
    {
      heading: 'Whose',
      label: 'Whose',
      value: mine ? 'mine' : 'everyone',
      usual: 'everyone',
      options: [
        ['everyone', 'Everyone’s'],
        ['mine', 'Only mine'],
      ],
      onChange: (w) => onChange({ mine: w === 'mine' }),
    },
  ]
  return (
    // In the phone's top bar, search gives up a little of its padding so "Search" still fits beside the "+".
    <BarRow
      className="flex items-center gap-2 nav:gap-3"
      barClassName="flex min-w-0 flex-1 items-center gap-2 [&_input]:pr-2 [&_input]:pl-9 [&_svg.absolute]:left-3"
    >
      {!loose && (
        <Pills
          label="Show recipes as"
          value={view}
          options={[
            ['books', 'Books'],
            ['all', 'Recipes'],
          ]}
          onChange={(v) => onChange({ view: v })}
          compact
        />
      )}
      <div className="relative min-w-0 flex-1 nav:max-w-sm">
        <Search className="absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input
          type="search"
          aria-label="Find a recipe"
          placeholder="Search"
          className="rounded-full border-transparent bg-secondary pl-10 shadow-none dark:bg-secondary"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>
      {!loose && <FilterButton title="Filter" filters={filters} />}
      <span className="flex-1 max-nav:hidden" />
      {select}
    </BarRow>
  )
}

// When you last looked at this circle's recipes on this browser, for the kitchens' rings: a kitchen with a recipe
// added since then is drawn in the logo's colours for this visit. Null the first time, so nothing is marked new.
function useSeen(familyId: string) {
  const key = `gatheredfrom:recipes-seen:${familyId}`
  const [seen] = useState(() => {
    try {
      const value = Number(localStorage.getItem(key))
      return value > 0 ? value : null
    } catch {
      return null
    }
  })
  useEffect(() => remember(key, String(Date.now())), [key])
  return seen
}
